import { action, observable } from "mobx";
import { RequestStatus } from "mobx-store";
import Connections from "../../connections";
import moment from "moment";

const querystring = require("querystring");

interface LeadDto {
  phonenumber1: string;
  phonenumber2: string;
  name: string;
  otp?: string;
  points?: string;
  membershipId?: string;
  redemptionMethod?: RedemptionMethod;
  winningDate: string;
  c4cId?: string;
  id: number;
}

interface LeadView {
  phonenumber1: string;
  phonenumber2: string;
  name: string;
  otp: string;
  points: string;
  id: number;
  winningDate: string;
}

interface FilterOptions {
  search: string;
  method: RedemptionMethod | "";
  status: LeadStatus | "";
  startDate?: string;
  endDate?: string;
}

export enum LeadStatus {
  NoAction = "NoAction",
  OtpSent = "OtpSent",
  CalimedPoints = "CalimedPoints",
}

export enum RedemptionMethod {
  Gift = "Gift",
  Points = "Points",
}

export class UpdateLeadDto {
  membershipId: string;
  redemptionMethod: RedemptionMethod;
}

export const DEFAULT_PAGE_SIZE = 15;

export class QueryLeadsDto {
  status?: LeadStatus | "";
  method?: RedemptionMethod | "";
  search?: string;
  pageNumber?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
}

export class LeadStore {
  @observable leads: LeadView[] = [];
  @observable uploadState = RequestStatus.IDLE;
  @observable filter: FilterOptions = {
    method: "",
    search: "",
    status: "",
    endDate: moment().endOf("day").toISOString(),
    startDate: moment().startOf("day").toISOString(),
  };
  @observable pagination: {
    limit: number;
    page: number;
    total: number;
  } = {
    limit: 0,
    page: 0,
    total: 0,
  };

  @observable leadsState = RequestStatus.IDLE;
  @observable leadState = RequestStatus.IDLE;

  @action
  public async getLeads(): Promise<void> {
    try {
      const { limit, page } = this.pagination;
      const { method, search, status, startDate, endDate } = this.filter;
      const query: QueryLeadsDto = {
        pageNumber: page || 1,
        pageSize: limit || DEFAULT_PAGE_SIZE,
        status,
        search,
        method,
      };
      if (status === LeadStatus.CalimedPoints) {
        query.endDate = endDate?.toString();
        query.startDate = startDate?.toString();
      }
      const queryParams = querystring.stringify(query);
      this.leadsState = RequestStatus.LOADING;

      const response = await Connections.wadiDeglaAxios.httpGet(
        `/leads?${queryParams}`
      );
      const { limit: limitRes, page: pageRes, total: totalRes } = response.data;
      this.pagination = { limit: limitRes, page: pageRes, total: totalRes };
      this.leads = (response.data?.data as LeadDto[]).map((l) => ({
        phonenumber1: l.phonenumber1,
        name: l.name || "_",
        phonenumber2: l.phonenumber2 || "_",
        otp: l?.otp || "_",
        points: l?.points || "_",
        membershipId: l?.membershipId || "_",
        redemptionMethod: l?.redemptionMethod || "_",
        c4cId: l?.c4cId || "_",
        id: l.id,
        winningDate: l.winningDate
          ? moment(l.winningDate).toLocaleString()
          : "_",
      }));
      this.leadsState = RequestStatus.SUCCESS;
    } catch (error) {
      this.leadsState = RequestStatus.FAILED;
      console.log(error);
      return Promise.reject(error);
    }
  }

  @action
  public async getLeadsCsv(): Promise<void> {
    try {
      const { method, search, status, startDate, endDate } = this.filter;
      const query: QueryLeadsDto = {
        status,
        search,
        method,
      };
      if (status === LeadStatus.CalimedPoints) {
        query.endDate = endDate?.toString();
        query.startDate = startDate?.toString();
      }
      const queryParams = querystring.stringify(query);
      this.leadsState = RequestStatus.LOADING;

      const csv = await Connections.wadiDeglaAxios.httpGet(
        `/leads?${queryParams}`,
        {
          responseType: "blob",
          headers: {
            "x-apicache-bypass": true,
            Accept: "text/csv",
          },
        }
      );
      saveAs(csv, "Leads.xlsx");
      this.leadsState = RequestStatus.SUCCESS;
    } catch (error) {
      this.leadsState = RequestStatus.FAILED;
      console.log(error);
      return Promise.reject(error);
    }
  }

  @action
  public async updateLead(id: number, body: UpdateLeadDto): Promise<void> {
    try {
      this.leadState = RequestStatus.LOADING;
      await Connections.wadiDeglaAxios.httpPut(`/leads/${id}`, body);
      this.leadState = RequestStatus.SUCCESS;
    } catch (error) {
      this.leadState = RequestStatus.FAILED;
      throw error;
    }
  }

  /**
   * Upload Excel (.xls / .xlsx) file containing leads.
   * Uses multipart/form-data to send file to backend.
   */
  @action
  public async uploadLeadsFile(file: File): Promise<void> {
    try {
      this.uploadState = RequestStatus.LOADING;
      const formData = new FormData();
      formData.append("file", file);

      await Connections.wadiDeglaAxios.httpPost("/leads", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      this.uploadState = RequestStatus.SUCCESS;
    } catch (error) {
      this.uploadState = RequestStatus.FAILED;
      console.error("Lead file upload failed:", error);
      return Promise.reject(error);
    }
  }
}
